import { SUPPORTED_CHAIN_IDS } from '@ui/constants/chain';
import { useDataChainId } from '@ui/hooks/datachain';
import { ChainId } from '@ui/types/chain';
import { create } from 'zustand';

type ClosingTradesState = {
  closingTradesMap: Record<ChainId, ClosingTrade[]>;
  useClosingTrades: () => ClosingTrade[];
  addClosingTrade: (
    tradeHash: string,
    chainId: ChainId,
    positionTimestamp?: number,
  ) => void;
  removeClosingTrade: (
    tradeHash: string,
    chainId: ChainId,
    positionTimestamp?: number,
  ) => void;
  resetClosingTrades: () => void;
};

export type ClosingTrade = {
  positionHash: string;
  positionTimestamp?: number;
  submittedAt: number;
};

const defaultMap = Object.fromEntries(
  SUPPORTED_CHAIN_IDS.map((id) => [id, [] as ClosingTrade[]]),
) as Record<ChainId, ClosingTrade[]>;

export const useClosingTradesStore = create<ClosingTradesState>((set, get) => ({
  closingTradesMap: defaultMap,
  useClosingTrades: () => {
    const chainId = useDataChainId();

    return get().closingTradesMap[chainId];
  },
  addClosingTrade: (tradeHash, chainId, positionTimestamp) =>
    set((state) => {
      const list = state.closingTradesMap[chainId] ?? [];
      if (
        list.some(
          (item) =>
            item.positionHash === tradeHash &&
            item.positionTimestamp === positionTimestamp,
        )
      ) {
        return state;
      }

      return {
        closingTradesMap: {
          ...state.closingTradesMap,
          [chainId]: [
            ...list,
            {
              positionHash: tradeHash,
              positionTimestamp,
              submittedAt: Date.now(),
            },
          ],
        },
      };
    }),
  removeClosingTrade: (tradeHash, chainId, positionTimestamp) =>
    set((state) => {
      const list = state.closingTradesMap[chainId] ?? [];

      return {
        closingTradesMap: {
          ...state.closingTradesMap,
          [chainId]: list.filter(
            (item) =>
              item.positionHash !== tradeHash ||
              (positionTimestamp !== undefined &&
                item.positionTimestamp !== positionTimestamp),
          ),
        },
      };
    }),
  resetClosingTrades: () =>
    set({
      closingTradesMap: defaultMap,
    }),
}));
